import {
  applyRulebookAttachmentChange,
  ICreateCharacterRequest,
  NotificationType,
  useCharacter,
  useLorebook,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import type { RulebookAttachmentChange } from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachmentSelection";
import { useMedia } from "@next-platform/client-common-spicy-ui";
import { Lorebook } from "@next-platform/packages-ts-types";
import { Spacer, Spinner } from "@nextui-org/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import TextStyles from "@/components/common/TextStyles";
import { Grid, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, getDraftStorageKey } from "@/constants";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";
import { cn } from "@/utils";

import CharacterForm from "./components/CharacterForm";
import CreateCharacterHeroSection from "./components/CreateCharacterHeroSection";
import CreateCharacterLockedOverlay from "./components/CreateCharacterLockedOverlay";
import CreateCharacterPerksSection from "./components/CreateCharacterPerksSection";

const EMPTY_CHARACTER_FORM_TAGS: string[] = [];

export default function CreateCharacter() {
  const { onUserCreateCharacter, errorMessage: submitError, errorCode } = useCharacter();
  const navigate = useNavigate();
  const { draftId } = useParams();
  const { userId } = useUserInfo();
  const draftKey = getDraftStorageKey(userId);
  const storedData = useMemo(() => {
    if (!draftId) return null;
    try {
      return JSON.parse(localStorage.getItem(draftKey) as string)?.[draftId] ?? null;
    } catch {
      return null;
    }
  }, [draftKey, draftId]);
  const { isAuthenticated, isLoading } = useAuth();
  const applicationId = basicAppConfig.applicationId;
  const { isMobile } = useMedia();
  const [stateIsSubmitForManualReview, setStateIsSubmitForManualReview] = useState(false);
  const { onUserAttachLorebook } = useLorebook();
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const [stateIsSubmitting, setStateIsSubmitting] = useState(false);
  const shouldRedirectToEdit = Boolean(storedData?.moderationReport || storedData?.isEdit);

  useEffect(() => {
    if (shouldRedirectToEdit) {
      navigate(`/chatbot/edit/${storedData?.id || draftId}`, { replace: true });
    }
  }, [draftId, navigate, shouldRedirectToEdit, storedData?.id]);

  const handleCharacterFormSave = useCallback(
    async (
      props: ICreateCharacterRequest,
      currentLorebooks: Lorebook[],
      initialLorebooks?: Lorebook[],
      rulebookChange?: RulebookAttachmentChange,
    ) => {
      setStateIsSubmitting(true);
      const requestProps = stateIsSubmitForManualReview ? { ...props, submitForManualReview: true } : props;

      const characterCreateResult = await onUserCreateCharacter(requestProps);
      const createdCharacter = characterCreateResult.data;
      const { error, errorPayload } = characterCreateResult;

      if (createdCharacter?.id) {
        if (currentLorebooks?.[0]?.id) {
          await onUserAttachLorebook(currentLorebooks?.[0]?.id, { characterId: createdCharacter.id });
        }
        if (rulebookChange?.currentRulebookId) {
          // The character exists either way; a failed attach is reported but doesn't block opening the chat.
          await applyRulebookAttachmentChange({
            ownerType: "character",
            ownerId: createdCharacter.id,
            initialRulebookId: null,
            currentRulebookId: rulebookChange.currentRulebookId,
          }).catch(() =>
            displayToast({
              message: t(
                "chatbot:form.section.rulebook.attachFailedOnCreate",
                "The chatbot was created, but its Rulebook couldn't be attached. Attach it again from Edit.",
              ),
              type: NotificationType.error,
            }),
          );
        }
        navigate(`/chat/${createdCharacter.id}`);
      }

      if (errorPayload?.data?.submitForManualReview === true) {
        setStateIsSubmitForManualReview(true);
      }
      setStateIsSubmitting(false);
      const characterResponseKey = "data";
      return { [characterResponseKey]: createdCharacter, error };
    },
    [displayToast, navigate, onUserAttachLorebook, onUserCreateCharacter, stateIsSubmitForManualReview, t],
  );

  const handleCharacterFormCancel = useCallback(() => {
    navigate("/");
  }, [navigate]);

  if (shouldRedirectToEdit) {
    return (
      <PageLayout size={PageSize.Medium}>
        <div className="flex justify-center items-center h-96">
          <Spinner />
        </div>
      </PageLayout>
    );
  }

  if (isLoading) {
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <PageLayout size={PageSize.Medium}>
      <PageStaticMetaTags pageId="createCharacter" data-testid="ChatbotCreate-PageStaticMetaTags" />
      {!isAuthenticated && (
        <>
          <CreateCharacterHeroSection />
          <CreateCharacterPerksSection />
        </>
      )}
      <div
        className={cn(
          "flex flex-col relative bg-gray-1 dark:bg-gray-3 p-lg max-mob:p-md rounded-[16px] border border-solid border-gray-5",
          !isAuthenticated && "py-16",
        )}
      >
        {!isAuthenticated ? <CreateCharacterLockedOverlay /> : null}
        <Spacer y={0.5} />
        <Grid className={cn("flex justify-between", isMobile ? "flex-col" : "flex-row")}>
          <Text variant={"xxl"} b>
            <Trans i18nKey="chatbot:page.create.title" />
          </Text>
          <LocalizedLink
            className="flex items-center text-link"
            target="_blank"
            to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.creation_guide}
            aria-label="chatbot-guide"
          >
            <Trans i18nKey="chatbot:page.create.creationGuide" />
          </LocalizedLink>
        </Grid>
        <Spacer y={0.5} />
        {draftId && !storedData ? (
          <Spinner />
        ) : (
          <CharacterForm
            {...storedData}
            tags={storedData?.tags ?? EMPTY_CHARACTER_FORM_TAGS}
            groupAddable={storedData?.group_addable ?? true}
            onSave={handleCharacterFormSave}
            onCancel={handleCharacterFormCancel}
            allowImport={true}
            draftId={draftId}
            isSubmitting={stateIsSubmitting}
            submitError={submitError}
            errorCode={errorCode}
            submitForManualReview={stateIsSubmitForManualReview}
            setIsSubmitForManualReview={setStateIsSubmitForManualReview}
          />
        )}
      </div>
    </PageLayout>
  );
}
